import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from './utils/api';

function CustomerSitesTab({ customerId, sites, onAddSite, onEditSite, onDeleteSite, onRefresh }) {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const columnMenuRef = useRef(null);

  // Column visibility state - load from API
  const [visibleColumns, setVisibleColumns] = useState({
    siteName: true,
    siteRef: false,
    contact: true,
    phone: true,
    mobile: false,
    email: false,
    address: true,
    city: true,
    county: false,
    postcode: false,
    accessNotes: false,
    isPrimary: false,
  });

  // Available columns configuration
  const columns = [
    { key: 'siteName', label: 'Site Name' },
    { key: 'siteRef', label: 'Site Ref' },
    { key: 'contact', label: 'Contact Name' },
    { key: 'phone', label: 'Phone' },
    { key: 'mobile', label: 'Mobile' },
    { key: 'email', label: 'Email' },
    { key: 'address', label: 'Address' },
    { key: 'city', label: 'City' },
    { key: 'county', label: 'County' },
    { key: 'postcode', label: 'Postcode' },
    { key: 'accessNotes', label: 'Access Notes' },
    { key: 'isPrimary', label: 'Primary Site' },
  ];

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const response = await apiFetch(`/api/preferences/default/customer-sites-${customerId}`);
      const data = await response.json();
      
      if (data.preferences) {
        setVisibleColumns(data.preferences);
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    }
  };

  const savePreferences = async (newPreferences) => {
    try {
      await apiFetch(`/api/preferences/default/customer-sites-${customerId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preferences: newPreferences })
      });
    } catch (error) {
      console.error('Error saving preferences:', error);
    }
  };

  useEffect(() => {
    savePreferences(visibleColumns);
  }, [visibleColumns]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (columnMenuRef.current && !columnMenuRef.current.contains(event.target)) {
        setShowColumnMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleColumn = (columnKey) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  const filteredSites = sites.filter(site =>
    site.site_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    site.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    site.city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const visibleColumnCount = Object.values(visibleColumns).filter(Boolean).length + 1;

  return (
    <div className="p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Sites</h2>
        <button 
          onClick={onAddSite}
          className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium shadow-sm transition"
        >
          ➕ Add Site
        </button>
      </div>

      {sites.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">📍</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No sites yet</h3>
          <p className="text-gray-500 mb-4">Add your first site to get started</p>
        </div>
      ) : (
        <>
          {/* Search + Column Selector */}
          <div className="mb-3 flex items-center space-x-2">
            <input
              type="text"
              placeholder="Search sites..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 max-w-md px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-green-500 focus:border-green-500"
            />
            
            {/* Column Visibility Button */}
            <div className="relative" ref={columnMenuRef}>
              <button
                onClick={() => setShowColumnMenu(!showColumnMenu)}
                className="px-3 py-1.5 bg-white border border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-50 transition flex items-center space-x-1"
              >
                <span>⚙️</span>
                <span>Columns</span>
              </button>

              {showColumnMenu && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 w-48">
                  <div className="p-2">
                    <div className="text-xs font-semibold text-gray-600 mb-2 px-2">Show Columns</div>
                    {columns.map(column => (
                      <label
                        key={column.key}
                        className="flex items-center px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={visibleColumns[column.key]}
                          onChange={() => toggleColumn(column.key)}
                          className="mr-2 rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                        <span className="text-sm text-gray-700">{column.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {visibleColumns.siteName && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600">Site Name</th>}
                  {visibleColumns.siteRef && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600">Site Ref</th>}
                  {visibleColumns.contact && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600">Contact</th>}
                  {visibleColumns.phone && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600">Phone</th>}
                  {visibleColumns.mobile && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600">Mobile</th>}
                  {visibleColumns.email && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600">Email</th>}
                  {visibleColumns.address && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600">Address</th>}
                  {visibleColumns.city && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600">City</th>}
                  {visibleColumns.county && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600">County</th>}
                  {visibleColumns.postcode && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600">Postcode</th>}
                  {visibleColumns.accessNotes && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600">Access Notes</th>}
                  {visibleColumns.isPrimary && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600">Primary</th>}
                  <th className="text-right px-3 py-1.5 text-xs font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredSites.length === 0 ? (
                  <tr>
                    <td colSpan={visibleColumnCount} className="px-3 py-6 text-center text-gray-500 text-sm">
                      {searchTerm ? 'No matches.' : 'No sites.'}
                    </td>
                  </tr>
                ) : (
                  filteredSites.map((site, index) => (
                    <tr
                      key={site.id}
                      onClick={() => navigate(`/customers/${customerId}/sites/${site.id}`)}
                      className={`hover:bg-blue-200 cursor-pointer transition ${index % 2 === 1 ? 'bg-blue-100' : 'bg-white'}`}
                    >
                      {visibleColumns.siteName && (
                        <td className="px-3 py-2">
                          <div className="font-medium text-gray-900">{site.site_name}</div>
                        </td>
                      )}
                      {visibleColumns.siteRef && <td className="px-3 py-2 text-gray-700">{site.site_ref || '-'}</td>}
                      {visibleColumns.contact && <td className="px-3 py-2 text-gray-700">{site.contact_name || '-'}</td>}
                      {visibleColumns.phone && <td className="px-3 py-2 text-gray-700">{site.phone || '-'}</td>}
                      {visibleColumns.mobile && <td className="px-3 py-2 text-gray-700">{site.mobile || '-'}</td>}
                      {visibleColumns.email && <td className="px-3 py-2 text-gray-700">{site.email || '-'}</td>}
                      {visibleColumns.address && <td className="px-3 py-2 text-gray-700">{site.address || '-'}</td>}
                      {visibleColumns.city && <td className="px-3 py-2 text-gray-700">{site.city || '-'}</td>}
                      {visibleColumns.county && <td className="px-3 py-2 text-gray-700">{site.county || '-'}</td>}
                      {visibleColumns.postcode && <td className="px-3 py-2 text-gray-700">{site.postcode || '-'}</td>}
                      {visibleColumns.accessNotes && <td className="px-3 py-2 text-gray-700 text-xs">{site.access_notes ? (site.access_notes.length > 50 ? site.access_notes.substring(0, 50) + '...' : site.access_notes) : '-'}</td>}
                      {visibleColumns.isPrimary && <td className="px-3 py-2 text-center">{site.is_primary ? '✓' : ''}</td>}
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/customers/${customerId}/sites/${site.id}`);
                          }}
                          className="text-green-600 hover:text-green-800 text-xs font-medium mr-3"
                        >
                          View
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditSite(site);
                          }}
                          className="text-blue-600 hover:text-blue-800 text-xs font-medium mr-3"
                        >
                          Edit
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm(`Delete site "${site.site_name}"?`)) {
                              onDeleteSite(site.id);
                            }
                          }}
                          className="text-red-600 hover:text-red-800 text-xs font-medium"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-2 text-xs text-gray-600">
            Showing {filteredSites.length} of {sites.length}
          </div>
        </>
      )}
    </div>
  );
}

export default CustomerSitesTab;
